import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function Profile() {
  const { user, refresh } = useAuth();
  const p = user?.profile || {};
  const [profile, setProfile] = useState({
    gender: p.gender || "",
    region: p.region || "Kerala",
    language: p.language || "en",
    height_cm: p.height_cm || "",
    weight_kg: p.weight_kg || "",
    blood_group: p.blood_group || "",
    allergies: (p.allergies || []).join(", "),
    conditions: (p.conditions || []).join(", "),
    goals: (p.goals || []).join(", "),
  });
  const [contacts, setContacts] = useState(p.emergency_contacts || [{ name: "", phone: "", relation: "" }]);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/profile", {
        ...profile,
        height_cm: profile.height_cm ? Number(profile.height_cm) : null,
        weight_kg: profile.weight_kg ? Number(profile.weight_kg) : null,
        allergies: profile.allergies.split(",").map((s) => s.trim()).filter(Boolean),
        conditions: profile.conditions.split(",").map((s) => s.trim()).filter(Boolean),
        goals: profile.goals.split(",").map((s) => s.trim()).filter(Boolean),
        emergency_contacts: contacts.filter((c) => c.phone),
      });
      await refresh();
      toast.success("Profile updated");
    } catch { toast.error("Could not save"); }
    finally { setSaving(false); }
  };

  return (
    <div className="p-6 md:p-12 pb-24 md:pb-12 max-w-3xl">
      <div className="mb-8">
        <div className="overline text-terracotta">Your health record</div>
        <h1 className="font-heritage text-5xl text-ayurveda mt-1">Profile</h1>
      </div>

      <Card className="border-black/5 bg-white mb-6">
        <CardContent className="p-6 space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div><Label>Name</Label><Input value={user?.name || ""} disabled data-testid="profile-name" /></div>
            <div><Label>Email</Label><Input value={user?.email || ""} disabled data-testid="profile-email" /></div>
            <div>
              <Label>Gender</Label>
              <Select value={profile.gender} onValueChange={(v) => setProfile({ ...profile, gender: v })}>
                <SelectTrigger data-testid="profile-gender"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="female">Female</SelectItem>
                  <SelectItem value="male">Male</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Blood group</Label><Input value={profile.blood_group} onChange={(e) => setProfile({...profile, blood_group: e.target.value})} data-testid="profile-blood" /></div>
            <div><Label>Height (cm)</Label><Input type="number" value={profile.height_cm} onChange={(e) => setProfile({...profile, height_cm: e.target.value})} data-testid="profile-height" /></div>
            <div><Label>Weight (kg)</Label><Input type="number" value={profile.weight_kg} onChange={(e) => setProfile({...profile, weight_kg: e.target.value})} data-testid="profile-weight" /></div>
            <div>
              <Label>Region</Label>
              <Select value={profile.region} onValueChange={(v) => setProfile({...profile, region: v})}>
                <SelectTrigger data-testid="profile-region"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Kerala">Kerala</SelectItem>
                  <SelectItem value="Malabar">Malabar</SelectItem>
                  <SelectItem value="South India">South India</SelectItem>
                  <SelectItem value="Pan-India">Pan-India</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Language</Label>
              <Select value={profile.language} onValueChange={(v) => setProfile({...profile, language: v})}>
                <SelectTrigger data-testid="profile-language"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="ml">Malayalam</SelectItem>
                  <SelectItem value="hi">Hindi</SelectItem>
                  <SelectItem value="ta">Tamil</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Allergies (comma separated)</Label><Input value={profile.allergies} onChange={(e) => setProfile({...profile, allergies: e.target.value})} data-testid="profile-allergies" /></div>
          <div><Label>Conditions</Label><Input value={profile.conditions} onChange={(e) => setProfile({...profile, conditions: e.target.value})} data-testid="profile-conditions" /></div>
          <div><Label>Health goals</Label><Textarea value={profile.goals} onChange={(e) => setProfile({...profile, goals: e.target.value})} data-testid="profile-goals" /></div>
        </CardContent>
      </Card>

      <Card className="border-black/5 bg-white mb-6">
        <CardContent className="p-6">
          <div className="overline text-terracotta mb-4">Emergency contacts</div>
          <div className="space-y-3">
            {contacts.map((c, i) => (
              <div key={i} className="grid grid-cols-3 gap-2">
                <Input placeholder="Name" value={c.name} onChange={(e) => { const n=[...contacts]; n[i].name=e.target.value; setContacts(n); }} data-testid={`profile-ec-name-${i}`} />
                <Input placeholder="Phone" value={c.phone} onChange={(e) => { const n=[...contacts]; n[i].phone=e.target.value; setContacts(n); }} data-testid={`profile-ec-phone-${i}`} />
                <Input placeholder="Relation" value={c.relation} onChange={(e) => { const n=[...contacts]; n[i].relation=e.target.value; setContacts(n); }} data-testid={`profile-ec-rel-${i}`} />
              </div>
            ))}
            <Button variant="outline" onClick={() => setContacts([...contacts, { name:"", phone:"", relation:"" }])} data-testid="profile-add-ec">+ Add contact</Button>
          </div>
        </CardContent>
      </Card>

      <Button onClick={save} disabled={saving} className="bg-ayurveda hover:bg-ayurveda/90 text-bone h-11 px-8" data-testid="profile-save">
        {saving ? "Saving…" : "Save changes"}
      </Button>
    </div>
  );
}
